import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Loader } from "@package/subframe/components/Loader";
import {
  FeatherArrowLeft,
  FeatherChevronRight,
  FeatherPlus,
} from "@subframe/core";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useState } from "react";

import { FeatureGate } from "@/components/shared/FeatureGate";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useOrganisation } from "@/contexts/OrganisationContext";
import { useListEmergencyLightChecksQueryControllerList } from "@/generated/emergency-light-checks/emergency-light-checks";
import {
  EmergencyLightCheckKind,
  type EmergencyLightCheckListItemDto,
  ListMonitoringEntriesQueryControllerListCheckSlug,
  ListMonitoringEntriesQueryControllerListScope,
  ListMonitoringEntriesQueryControllerListSortDir,
  type MonitoringEntryDto,
} from "@/generated/model";
import { useListMonitoringEntriesQueryControllerList } from "@/generated/monitoring/monitoring";
import {
  type PendingEmergencyLightCheck,
  usePendingEmergencyLightChecks,
} from "@/hooks/use-pending-emergency-light-checks";
import {
  EMERGENCY_LIGHT_CHECK_COPY,
  toEmergencyLightCheckKind,
} from "@/lib/emergency-light-checks";
import { FEATURES } from "@/lib/features";
import { memberDisplayName } from "@/lib/member-display";
import { canViewAllChecks } from "@/lib/roles";

const LEGACY_HISTORY_FROM = "2020-01-01T00:00:00.000Z";
const LEGACY_HISTORY_LIMIT = 200;
const HISTORY_LIMIT = 200;

type HistoryRow =
  | { kind: "check"; at: string; check: EmergencyLightCheckListItemDto }
  | { kind: "legacy"; at: string; entry: MonitoringEntryDto };

export const Route = createFileRoute("/_authenticated/emergency-light-checks/")(
  {
    validateSearch: (
      search: Record<string, unknown>,
    ): { kind: EmergencyLightCheckKind } => ({
      kind: toEmergencyLightCheckKind(search.kind),
    }),
    component: EmergencyLightCheckHistoryPageRoute,
  },
);

// Emergency lighting sits on Boost and above with the rest of Monitoring
// (NAU-435); a Launch nursery gets the upgrade prompt instead.
function EmergencyLightCheckHistoryPageRoute() {
  return (
    <FeatureGate feature={FEATURES.MONITORING} featureName="Monitoring">
      <EmergencyLightCheckHistoryPage />
    </FeatureGate>
  );
}

function EmergencyLightCheckHistoryPage() {
  const { kind } = Route.useSearch();
  const copy = EMERGENCY_LIGHT_CHECK_COPY[kind];
  const navigate = useNavigate();
  const { user, isPlatformAdmin } = useAuth();
  const { selectedNurseryId, nurseryRole } = useNursery();
  const { isOrgAdmin } = useOrganisation();

  // AC11: the two checks keep separate histories, so the list is always
  // narrowed to one kind rather than interleaving them.
  const { data, isLoading } = useListEmergencyLightChecksQueryControllerList(
    selectedNurseryId ?? "",
    // The server's maximum: a daily visual check would outgrow the default of
    // 50 in about seven weeks.
    { kind, limit: HISTORY_LIMIT },
    { query: { enabled: !!selectedNurseryId } },
  );
  const checks = data?.data?.items ?? [];
  const totalChecks = data?.data?.total ?? 0;
  const {
    checks: devicePendingChecks,
    discard,
    retry,
  } = usePendingEmergencyLightChecks(selectedNurseryId, kind);

  // Before per-light checks, emergency lighting was one pass/fail tick per room
  // (the `emergency-lighting` log type). Those records are kept, not converted,
  // so the flick test history shows them alongside, marked as the old format.
  const isFlickTest = kind === EmergencyLightCheckKind.FLICK_TEST;
  const { data: legacyData, isLoading: isLegacyLoading } =
    useListMonitoringEntriesQueryControllerList(
      selectedNurseryId ?? "",
      {
        scope: ListMonitoringEntriesQueryControllerListScope.history,
        checkSlug:
          ListMonitoringEntriesQueryControllerListCheckSlug[
            "emergency-lighting"
          ],
        // History defaults to the current month; legacy records are older.
        from: LEGACY_HISTORY_FROM,
        sortDir: ListMonitoringEntriesQueryControllerListSortDir.desc,
        limit: LEGACY_HISTORY_LIMIT,
      },
      // A failed read just leaves the legacy rows out.
      { query: { enabled: !!selectedNurseryId && isFlickTest, retry: false } },
    );
  // The API already limits new checks to the viewer's own for staff below
  // deputy manager; old entries are readable by every member, so apply the
  // same rule here rather than show a practitioner everyone's old records.
  const seesAllChecks = canViewAllChecks({
    isPlatformAdmin,
    isOrgAdmin,
    nurseryRole,
  });
  const legacyEntries = isFlickTest
    ? (legacyData?.data?.data ?? []).filter(
        (entry) => seesAllChecks || entry.recordedById === user?.id,
      )
    : [];
  // The same rule for checks still on this device: on a shared tablet a
  // practitioner shouldn't see, or discard, a colleague's queued check.
  const pendingChecks = devicePendingChecks.filter(
    (check) => seesAllChecks || check.capturedByUserId === user?.id,
  );

  const rows: HistoryRow[] = [
    ...checks.map((check) => ({
      kind: "check" as const,
      at: check.submittedAt,
      check,
    })),
    ...legacyEntries.map((entry) => ({
      kind: "legacy" as const,
      at: entry.triggeredAt,
      entry,
    })),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  return (
    <div className="flex w-full max-w-2xl flex-col items-start gap-6 bg-white animate-fade-in">
      <div className="flex w-full flex-wrap items-start gap-2">
        <IconButton
          aria-label="Back"
          icon={<FeatherArrowLeft />}
          onClick={() =>
            navigate({ to: "/checklists", search: { tab: "logs" } })
          }
        />
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
          <span className="w-full text-metrics font-metrics text-primary-navy-800">
            {copy.title}
          </span>
          <span className="text-body font-body text-subtext-color">
            {copy.subtitle}
          </span>
        </div>
        <Button
          variant="brand-primary"
          icon={<FeatherPlus />}
          onClick={() =>
            navigate({ to: "/emergency-light-checks/new", search: { kind } })
          }
        >
          New check
        </Button>
      </div>

      {isLoading || (isFlickTest && isLegacyLoading) ? (
        <div className="flex w-full items-center justify-center py-12">
          <Loader size="large" />
        </div>
      ) : rows.length === 0 && pendingChecks.length === 0 ? (
        <div className="flex w-full flex-col items-start gap-2 rounded-md border border-neutral-200 p-6">
          <span className="text-body font-body text-default-font">
            No checks recorded yet.
          </span>
        </div>
      ) : (
        <div className="flex w-full flex-col items-start gap-2">
          {pendingChecks.map((check) => (
            <PendingCheckRow
              key={check.id}
              check={check}
              onDiscard={() => void discard(check.id)}
              onRetry={() => void retry()}
            />
          ))}
          {rows.map((row) =>
            row.kind === "legacy" ? (
              <LegacyEntryRow key={row.entry.id} entry={row.entry} />
            ) : (
              <CheckRow key={row.check.id} check={row.check} />
            ),
          )}
          {totalChecks > checks.length && (
            <span className="text-caption font-caption text-subtext-color">
              Showing the latest {checks.length} of {totalChecks} checks. Use
              Monitoring → History to see older ones.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function CheckRow({ check }: { check: EmergencyLightCheckListItemDto }) {
  return (
    <Link
      to="/emergency-light-checks/$checkId"
      params={{ checkId: check.id }}
      className="w-full"
    >
      <div className="flex w-full items-center gap-3 rounded-lg border border-neutral-200 px-4 py-3 hover:bg-neutral-50 transition-colors cursor-pointer">
        <div className="flex grow flex-col items-start gap-0.5">
          <span className="text-body-bold font-body-bold text-default-font">
            {format(new Date(check.submittedAt), "d MMM yyyy, HH:mm")}
          </span>
          <span className="text-caption font-caption text-subtext-color">
            Logged by{" "}
            {check.recordedBy ? memberDisplayName(check.recordedBy) : "Unknown"}{" "}
            · {check.itemCount} light{check.itemCount === 1 ? "" : "s"}
          </span>
        </div>
        {check.failedCount > 0 ? (
          <Badge variant="error">{check.failedCount} failed</Badge>
        ) : (
          <Badge variant="success">All passed</Badge>
        )}
        <FeatherChevronRight className="h-4 w-4 text-subtext-color" />
      </div>
    </Link>
  );
}

/** Recorded on this device but not on the server yet, so there is no detail page. */
function PendingCheckRow({
  check,
  onDiscard,
  onRetry,
}: {
  check: PendingEmergencyLightCheck;
  onDiscard: () => void;
  onRetry: () => void;
}) {
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  const recordedAt = format(new Date(check.queuedAt), "d MMM yyyy, HH:mm");
  const isRejected = check.localSyncState === "rejected";
  const isStalled = check.localSyncState === "stalled";
  return (
    <div className="flex w-full items-center gap-3 rounded-lg border border-neutral-200 px-4 py-3">
      <div className="flex grow flex-col items-start gap-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-body-bold font-body-bold text-default-font">
            {recordedAt}
          </span>
          {check.localSyncState === "pending" ? (
            <Badge variant="neutral">Syncing…</Badge>
          ) : (
            <Badge variant="error">Sync failed</Badge>
          )}
        </div>
        <span className="text-caption font-caption text-subtext-color">
          Recorded on this device · {check.itemCount} light
          {check.itemCount === 1 ? "" : "s"}
          {check.failedCount > 0 ? ` · ${check.failedCount} failed` : ""}
        </span>
        {isRejected && check.lastError ? (
          <span className="text-caption font-caption text-error-700">
            {check.lastError}
          </span>
        ) : null}
        {isStalled ? (
          <span className="text-caption font-caption text-error-700">
            Couldn&apos;t reach the server. It&apos;s still saved on this
            device.
          </span>
        ) : null}
      </div>
      {isStalled ? (
        <Button
          variant="neutral-secondary"
          size="small"
          aria-label={`Retry the check recorded ${recordedAt}`}
          onClick={onRetry}
        >
          Retry
        </Button>
      ) : null}
      {/* The only copy of an inspection, so discarding takes a second tap. */}
      {isRejected && !isConfirmingDiscard ? (
        <Button
          variant="destructive-secondary"
          size="small"
          aria-label={`Discard the check recorded ${recordedAt}`}
          onClick={() => setIsConfirmingDiscard(true)}
        >
          Discard
        </Button>
      ) : null}
      {isRejected && isConfirmingDiscard ? (
        <div className="flex items-center gap-2">
          <Button
            variant="neutral-tertiary"
            size="small"
            onClick={() => setIsConfirmingDiscard(false)}
          >
            Keep
          </Button>
          <Button
            variant="destructive-primary"
            size="small"
            aria-label={`Confirm discarding the check recorded ${recordedAt}`}
            onClick={onDiscard}
          >
            Discard check
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** A room-level record from before per-light checks. Nothing to drill into. */
function LegacyEntryRow({ entry }: { entry: MonitoringEntryDto }) {
  return (
    <div className="flex w-full items-center gap-3 rounded-lg border border-dashed border-neutral-200 bg-neutral-50 px-4 py-3">
      <div className="flex grow flex-col items-start gap-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-body-bold font-body-bold text-default-font">
            {format(new Date(entry.triggeredAt), "d MMM yyyy, HH:mm")}
          </span>
          <Badge variant="neutral">Room-level record (old format)</Badge>
        </div>
        <span className="text-caption font-caption text-subtext-color">
          Logged by{" "}
          {entry.recordedBy ? memberDisplayName(entry.recordedBy) : "Unknown"}
          {entry.locationName ? ` · ${entry.locationName}` : ""}
        </span>
        {entry.isOutOfRange && entry.failureDetail ? (
          <span className="text-caption font-caption text-error-700">
            {entry.failureDetail}
          </span>
        ) : null}
      </div>
      {entry.isOutOfRange ? (
        <Badge variant="error">Failed</Badge>
      ) : (
        <Badge variant="success">Passed</Badge>
      )}
    </div>
  );
}
